// Account footer component for the library page

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
  ChevronRightIcon,
  ChevronsUpDownIcon,
  FileTextIcon,
  Link2Icon,
  LogInIcon,
  LogOutIcon,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { useSidebar } from '../../../components/ui/sidebar';
import { useAuth } from '../../../hooks';
import { analyticsService } from '../../../services/analyticsService';
import { cn } from '@/utilities/cn';
import { getUserDisplayName } from '@airhead-games/shared/utilities';
import type { UserAuthFields } from '@airhead-games/shared/types';
import type { LibraryDialog } from '../libraryRouteTypes';

type AccountFooterProps = {
  user: UserAuthFields | null;
  openDialog: (dialog: LibraryDialog) => void;
};

/** Match sidebar nav rows; override DropdownMenuItem's `**:text-accent-foreground` (icons + text). */
const librarySidebarDropdownItemClassName =
  'focus:bg-sidebar-accent focus:text-sidebar-accent-foreground data-[highlighted]:bg-sidebar-accent data-[highlighted]:text-sidebar-accent-foreground not-data-[variant=destructive]:focus:**:text-sidebar-accent-foreground not-data-[variant=destructive]:data-[highlighted]:**:text-sidebar-accent-foreground';

const AccountFooter: React.FC<AccountFooterProps> = ({ user, openDialog }) => {
  const location = useLocation();
  const { isMobile, state } = useSidebar();
  const collapsedDesktop = !isMobile && state === 'collapsed';
  const { logUserOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) {
    return (
      <Button
        type="button"
        variant="default"
        size={collapsedDesktop ? 'icon-lg' : 'xl'}
        className={cn(
          'rounded-xl shadow-sm',
          collapsedDesktop ? 'mx-auto' : 'w-full justify-start'
        )}
        title="Login"
        onClick={() => {
          analyticsService.trackButtonClick('Login', {
            link_location: 'library_footer',
          });
          analyticsService.trackSidebarItemClick('Login', {
            surface: 'library_account',
            link_location: 'library_footer',
            navigation_target: 'login',
            link_type: 'internal',
            source_path: location.pathname,
            is_mobile: isMobile,
          });
          openDialog('login');
        }}
      >
        <LogInIcon data-icon="inline-start" />
        {!collapsedDesktop && 'Login'}
        {collapsedDesktop && <span className="sr-only">Login</span>}
      </Button>
    );
  }

  const displayName = getUserDisplayName(user);

  const trigger = (
    <button
      type="button"
      title="Profile"
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border border-sidebar-border bg-background px-3 py-3 text-left shadow-sm transition hover:bg-accent',
        collapsedDesktop && 'justify-center px-2'
      )}
    >
      <Avatar>
        <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      {!collapsedDesktop && (
        <>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{displayName}</div>
            <div className="text-xs text-muted-foreground">Account</div>
          </div>
          {isMobile ? (
            <ChevronRightIcon
              className={cn(
                'text-muted-foreground transition-transform',
                menuOpen && 'rotate-90'
              )}
            />
          ) : (
            <ChevronsUpDownIcon className="text-muted-foreground" />
          )}
        </>
      )}
      {collapsedDesktop && <span className="sr-only">Profile</span>}
    </button>
  );

  const handleLogOut = () => {
    analyticsService.trackButtonClick('Log Out', {
      link_location: 'library_connections',
    });
    trackAccountMenuItemClick('Log Out', 'logout');
    void logUserOut();
  };

  const trackAccountMenuOpenChange = (nextOpen: boolean) => {
    analyticsService.trackNavigationMenuToggle(
      'library_account',
      nextOpen ? 'open' : 'closed',
      {
        surface: 'library_account',
        link_location: 'library_footer',
        source_path: location.pathname,
        is_mobile: isMobile,
      }
    );
    setMenuOpen(nextOpen);
  };

  const trackAccountMenuItemClick = (label: string, target: string) => {
    analyticsService.trackSidebarItemClick(label, {
      surface: 'library_account',
      link_location: 'library_footer',
      navigation_target: target,
      link_type: 'internal',
      source_path: location.pathname,
      is_mobile: isMobile,
    });
  };

  const openTrackedDialog = (label: string, dialog: LibraryDialog) => {
    trackAccountMenuItemClick(label, dialog);
    openDialog(dialog);
  };

  if (isMobile) {
    return (
      <Collapsible
        open={menuOpen}
        onOpenChange={trackAccountMenuOpenChange}
        className="group/collapsible"
      >
        <CollapsibleTrigger asChild>{trigger}</CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="flex flex-col gap-1 rounded-xl border border-sidebar-border bg-background p-2 shadow-sm">
            <div className="px-2 py-2">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarFallback>
                    {displayName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {displayName}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              className="justify-start"
              onClick={() => openTrackedDialog('Connections', 'connections')}
            >
              <Link2Icon />
              Connections
            </Button>
            <Button variant="ghost" className="justify-start" asChild>
              <Link
                to="/terms"
                onClick={() =>
                  trackAccountMenuItemClick('Terms and Conditions', '/terms')
                }
              >
                <FileTextIcon />
                Terms and conditions
              </Link>
            </Button>
            <Button
              variant="ghost"
              className="justify-start text-destructive hover:text-destructive"
              onClick={handleLogOut}
            >
              <LogOutIcon />
              Log out
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={trackAccountMenuOpenChange}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="right"
        sideOffset={8}
        className="w-72"
      >
        <DropdownMenuLabel className="px-2 py-2">
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarFallback>
                {displayName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{displayName}</div>
              <div className="truncate text-xs text-muted-foreground">
                {user.email}
              </div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className={librarySidebarDropdownItemClassName}
          onClick={() => openTrackedDialog('Connections', 'connections')}
        >
          <Link2Icon />
          Connections
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className={librarySidebarDropdownItemClassName}
        >
          <Link
            to="/terms"
            onClick={() =>
              trackAccountMenuItemClick('Terms and Conditions', '/terms')
            }
          >
            <FileTextIcon />
            Terms and conditions
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive" onClick={handleLogOut}>
          <LogOutIcon />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccountFooter;
